import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../services/authFetch';

const TeacherLaporanLemburPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [user, setUser] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [teacherUsers, setTeacherUsers] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [teacherSuggestions, setTeacherSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);

  // Real data for overtime reports
  const [suratIjinRequests, setSuratIjinRequests] = useState([]);

  useEffect(() => {
    async function fetchReports() {
      try {
        const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
        
        // Use teacher endpoint instead of admin endpoint
        const res = await authFetch(`${apiBase}/api/teacher/overtime-reports`, {
          headers: { 'Accept': 'application/json' }
        });
        
        if (!res.ok) {
          const errorText = await res.text();
          console.error('API Error Response:', errorText);
          throw new Error(`Failed to fetch reports: ${res.status} ${errorText}`);
        }
        
        const data = await res.json();
        
        // Handle both Laravel Resource Collection format and direct array
        const sourceData = data.data || data || [];
        
        // Map backend data to UI format for overtime reports
        const mapped = sourceData.map(item => {
          return {
            id: item.id,
            // Store both formatted date for display and ISO date for form inputs
            tanggalPengajuan: item.created_at ? new Date(item.created_at).toLocaleDateString('id-ID', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            }) : '',
            // Keep original date in ISO format for date inputs
            tanggalPengajuanISO: item.created_at ? item.created_at.slice(0,10) : '',
            // Student name field is correct
            siswaMengajukan: item.student ? item.student.name : '',
            // Student has 'class' field, not 'kelas'
            kelas: item.student ? (item.student.class || '') : '',
            nis: item.student ? (item.student.nis || '') : '',
            // Overtime report specific fields
            tanggalLembur: item.overtime_date || '',
            tanggalLemburFormatted: item.overtime_date ? new Date(item.overtime_date).toLocaleDateString('id-ID', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            }) : '',
            tanggalLemburSelesai: item.end_overtime_date || '',
            jamMulai: item.start_time || '',
            jamSelesai: item.finish_time || '',
            jumlahJamLembur: item.overtime_hours || '',
            alasan: item.purpose || '',
            jenisPekerjaan: item.type_or_work || '',
            refleksi: item.reflection || '',
            guruBengkel: item.teacher_name || '',
            // Use request_status field instead of status
            status: item.request_status || item.status || 'menunggu',
            // Add images array
            images: item.images || [],
            decision_by_id: item.decision_by_id,
            decision_by_teacher_id: item.decision_by_teacher_id,
          };
        });
  
        setSuratIjinRequests(mapped);
      } catch (e) {
        setNotification({
          show: true,
          message: `Error fetching data: ${e.message}`,
          type: 'error'
        });
        
        // Auto-hide error notification after 5 seconds
        setTimeout(() => {
          setNotification({ show: false, message: '', type: 'success' });
        }, 5000);
        
        setSuratIjinRequests([]);
      }
    }
    fetchReports();
    
    // Fetch teachers for auto-suggest
    async function fetchTeachers() {
      try {
        const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
        const res = await authFetch(`${apiBase}/api/student/teachers`, {
          headers: { 'Accept': 'application/json' }
        });
        if (res.ok) {
          const data = await res.json();
          setTeachers(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Error fetching teachers:', error);
        setTeachers([]);
      }
    }
    fetchTeachers();
    
    async function fetchAdminUsers() {
      try {
        const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
        const res = await authFetch(`${apiBase}/api/teacher/admin-users`, {
          headers: { 'Accept': 'application/json' }
        });
        if (res.ok) {
          const data = await res.json();
          setAdminUsers(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Error fetching admin users:', error);
        setAdminUsers([]);
      }
    }
    fetchAdminUsers();
    
    // eslint-disable-next-line
  }, [user]);

  const filteredRequests = suratIjinRequests.filter(request => {
    const q = searchQuery.toLowerCase();
    return (
      (request.siswaMengajukan && request.siswaMengajukan.toLowerCase().includes(q)) ||
      (request.kelas && request.kelas.toLowerCase().includes(q)) ||
      (request.nis && request.nis.toLowerCase().includes(q)) ||
      (request.guruBengkel && request.guruBengkel.toLowerCase().includes(q)) ||
      (request.alasan && request.alasan.toLowerCase().includes(q)) ||
      (request.jenisPekerjaan && request.jenisPekerjaan.toLowerCase().includes(q))
    );
  });

  // Fetch user data from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Helper function to get the name of the decision maker
  const getDecisionMakerName = (permit) => {
    // Check if the decision was made by a teacher
    if (permit.decision_by_teacher_id) {
      // First, check if the decision maker is the currently logged-in teacher
      if (user && user.id === permit.decision_by_teacher_id) {
        return user.name;
      }
      // If not, search in the general list of teachers (as a fallback)
      const teacher = teachers.find(t => t.id === permit.decision_by_teacher_id);
      return teacher ? teacher.name : 'Guru Bengkel';
    }
    
    // Check if the decision was made by an admin
    if (permit.decision_by_id) {
      const admin = adminUsers.find(a => a.id === permit.decision_by_id);
      return admin ? admin.name : 'Admin';
    }
    
    // Fallback for pending status or older records without decision info
    return 'Belum diputuskan';
  };

  // Teacher auto-suggest handlers
  const handleTeacherInputChange = (value) => {
    setSelectedPermit({ ...selectedPermit, guruBengkel: value, teacher_id: '' });
    setSelectedTeacherId(null);
    
    // Filter teachers based on input
    if (value.trim()) {
      const filtered = teachers.filter(teacher => 
        teacher.name.toLowerCase().includes(value.toLowerCase())
      );
      setTeacherSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setTeacherSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleTeacherSelect = (teacher) => {
    setSelectedPermit({ 
      ...selectedPermit, 
      guruBengkel: teacher.name,
      teacher_id: teacher.id 
    });
    setSelectedTeacherId(teacher.id);
    setShowSuggestions(false);
    setTeacherSuggestions([]);
  };

  const handleTeacherInputBlur = () => {
    // Delay hiding suggestions to allow for click
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  return (
    <PageContainer>
      <PageHeader>
        <HeaderContent>
          <div>
            <BackButton onClick={() => navigate('/teacher')}>
              ← Kembali
            </BackButton>
            <h1>Laporan Lembur</h1>
            <Subtitle>Kelola dan tinjau laporan lembur siswa bengkel</Subtitle>
          </div>
        </HeaderContent>
      </PageHeader>

      <ContentSection>
        <ToolbarSection>
          <SearchContainer>
            <SearchIcon>🔍</SearchIcon>
            <SearchInput
              type="text"
              placeholder="Cari berdasarkan nama siswa, kelas, atau mata pelajaran..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchContainer>
        </ToolbarSection>

        <TableContainer>
          <Table>
            <TableHeader>
              <tr>
                <th>Tanggal Pengajuan</th>
                <th>Siswa</th>
                <th>Kelas</th>
                <th>Tanggal Lembur</th>
                <th>Jam Mulai</th>
                <th>Jam Selesai</th>
                <th>Alasan</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </TableHeader>
            <TableBody>
              {filteredRequests.length > 0 ? (
                filteredRequests.map((request) => (
                  <TableRow
                    key={request.id}
                    onClick={() => setSelectedPermit(request)}
                    selected={selectedPermit?.id === request.id}
                  >
                    <td>{request.tanggalPengajuan}</td>
                    <td>{request.siswaMengajukan}</td>
                    <td>{request.kelas}</td>
                    <td>{request.tanggalLemburFormatted}</td>
                    <td>{request.jamMulai}</td>
                    <td>{request.jamSelesai}</td>
                    <td>{request.alasan}</td>
                    <td>
                      <StatusBadge status={request.status}>
                        {request.status === 'pending' || request.status === 'menunggu' ? 'Menunggu' :
                          request.status === 'approved' || request.status === 'disetujui' ? 'Disetujui' : 'Ditolak'}
                      </StatusBadge>
                    </td>
                    <td>
                      <ActionButtons>
                        <ActionButton onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPermit(request);
                        }} title="Lihat Detail">
                          👁️
                        </ActionButton>
                      </ActionButtons>
                    </td>
                  </TableRow>
                ))
              ) : (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '1rem' }}>
                    Tidak ada laporan lembur untuk bengkel Anda
                  </td>
                </tr>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </ContentSection>

      {selectedPermit && (
        <DetailModal onClick={() => setSelectedPermit(null)}>
          <DetailCard onClick={e => e.stopPropagation()}>
            <DetailHeader>
              <DetailHeaderContent>
                <h2>Detail Laporan Lembur</h2>
                <StatusContainer>
                  <DetailStatus status={selectedPermit.status}>
                    {selectedPermit.status === 'pending' || selectedPermit.status === 'menunggu' ? 'Menunggu' :
                      selectedPermit.status === 'approved' || selectedPermit.status === 'disetujui' ? 'Disetujui' : 'Ditolak'}
                  </DetailStatus>
                  {selectedPermit.status !== 'pending' && selectedPermit.status !== 'menunggu' && (
                    <DecisionMaker>
                      Diputuskan oleh: {getDecisionMakerName(selectedPermit)}
                    </DecisionMaker>
                  )}
                </StatusContainer>
              </DetailHeaderContent>
              <CloseButton onClick={() => setSelectedPermit(null)}>×</CloseButton>
            </DetailHeader>

            <DetailContent>
              <DetailSection>
                <DetailGrid>
                  <DetailGroup>
                    <DetailLabel>Tanggal Pengajuan</DetailLabel>
                    <input
                      type="date"
                      value={selectedPermit.tanggalPengajuanISO || ''}
                      onChange={e => {
                        const newDate = e.target.value;
                        // Update both ISO and formatted date
                        setSelectedPermit({ 
                          ...selectedPermit, 
                          tanggalPengajuanISO: newDate,
                          tanggalPengajuan: newDate ? new Date(newDate).toLocaleDateString('id-ID', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          }) : ''
                        });
                      }}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Siswa yang mengajukan</DetailLabel>
                    <input
                      type="text"
                      value={selectedPermit.siswaMengajukan || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, siswaMengajukan: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Kelas</DetailLabel>
                    <input
                      type="text"
                      value={selectedPermit.kelas || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, kelas: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>NIS</DetailLabel>
                    <input
                      type="text"
                      value={selectedPermit.nis || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, nis: e.target.value })}
                    />
                  </DetailGroup>
                </DetailGrid>
              </DetailSection>

              <DetailSection>
                <DetailGrid>
                  <DetailGroup>
                    <DetailLabel>Tanggal Lembur Mulai</DetailLabel>
                    <input
                      type="date"
                      value={selectedPermit.tanggalLembur || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, tanggalLembur: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Tanggal Lembur Selesai</DetailLabel>
                    <input
                      type="date"
                      value={selectedPermit.tanggalLemburSelesai || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, tanggalLemburSelesai: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Jam Mulai</DetailLabel>
                    <input
                      type="time"
                      value={selectedPermit.jamMulai || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, jamMulai: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Jam Selesai</DetailLabel>
                    <input
                      type="time"
                      value={selectedPermit.jamSelesai || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, jamSelesai: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Jumlah Jam Lembur</DetailLabel>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={selectedPermit.jumlahJamLembur || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, jumlahJamLembur: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup full>
                    <DetailLabel>Alasan</DetailLabel>
                    <textarea
                      value={selectedPermit.alasan || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, alasan: e.target.value })}
                      style={{ width: '100%', minHeight: 60 }}
                    />
                  </DetailGroup>
                  <DetailGroup full>
                    <DetailLabel>Jenis Pekerjaan</DetailLabel>
                    <textarea
                      value={selectedPermit.jenisPekerjaan || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, jenisPekerjaan: e.target.value })}
                      style={{ width: '100%', minHeight: 60 }}
                    />
                  </DetailGroup>

                  <DetailGroup>
                    <DetailLabel>Nama Guru Bengkel</DetailLabel>
                    <TeacherSuggestContainer>
                      <input
                        type="text"
                        value={selectedPermit.guruBengkel || ''}
                        onChange={e => handleTeacherInputChange(e.target.value)}
                        onBlur={handleTeacherInputBlur}
                        onFocus={() => {
                          if (selectedPermit.guruBengkel?.trim() && teacherSuggestions.length > 0) {
                            setShowSuggestions(true);
                          }
                        }}
                        placeholder="Ketik nama guru..."
                        autoComplete="off"
                      />
                      {showSuggestions && teacherSuggestions.length > 0 && (
                        <SuggestionsDropdown>
                          {teacherSuggestions.map((teacher) => (
                            <SuggestionItem
                              key={teacher.id}
                              onClick={() => handleTeacherSelect(teacher)}
                              selected={selectedTeacherId === teacher.id}
                            >
                              <TeacherName>{teacher.name}</TeacherName>
                            </SuggestionItem>
                          ))}
                        </SuggestionsDropdown>
                      )}
                    </TeacherSuggestContainer>
                  </DetailGroup>
                </DetailGrid>
              </DetailSection>

              {/* Images Section */}
              {selectedPermit.images && selectedPermit.images.length > 0 && (
                <DetailSection>
                  <DetailLabel>Lampiran Gambar</DetailLabel>
                  <ImagesGrid>
                    {selectedPermit.images.map((image, index) => {
                      // Handle different image formats similar to SicknessForm
                      let imgSrc = '';
                      if (typeof image === 'string') {
                        imgSrc = image;
                      } else if (image && image.image_url) {
                        // API returns image objects with image_url property
                        const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                        imgSrc = image.image_url.startsWith('http') 
                          ? image.image_url 
                          : `${baseUrl}/${image.image_url}`;
                      }
                      
                      return (
                        <ImageContainer key={index}>
                          <img
                            src={imgSrc}
                            alt={`Lampiran ${index + 1}`}
                            onClick={() => window.open(imgSrc, '_blank')}
                            onError={(e) => {
                              console.error('Image failed to load:', imgSrc);
                              e.target.style.display = 'none';
                            }}
                          />
                        </ImageContainer>
                      );
                    })}
                  </ImagesGrid>
                </DetailSection>
              )}

            </DetailContent>
          </DetailCard>
        </DetailModal>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <NotificationToast type={notification.type}>
          {notification.message}
        </NotificationToast>
      )}
    </PageContainer>
  );
};

// Styled components (same as AdminLaporanLemburPage)
const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  min-height: calc(100vh - 60px);
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const Subtitle = styled.p`
  color: var(--text-light);
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
`;

const ContentSection = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  box-shadow: var(--box-shadow);
  overflow: hidden;
`;

const ToolbarSection = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border);
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  max-width: 500px;
  background-color: var(--background-light);
  border-radius: 8px;
  padding: 0.5rem 1rem;
`;

const SearchIcon = styled.span`
  margin-right: 0.5rem;
  color: var(--text-light);
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: none;
  font-size: 0.875rem;
  color: var(--text-dark);
  outline: none;

  &::placeholder {
    color: var(--text-light);
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: var(--background-light);
  
  th {
    padding: 1rem;
    text-align: left;
    font-weight: 600;
    color: var(--text-dark);
    font-size: 0.875rem;
    border-bottom: 1px solid var(--border);
  }
`;

const TableBody = styled.tbody`
  tr:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }
`;

const TableRow = styled.tr`
  cursor: pointer;
  background-color: ${props => props.selected ? 'var(--background-light)' : 'transparent'};
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }

  td {
    padding: 1rem;
    font-size: 0.875rem;
    color: var(--text-dark);
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => {
    switch (props.status) {
      case 'pending':
      case 'menunggu': return '#fff3e0';
      case 'approved':
      case 'disetujui': return '#e6f8f5';
      case 'rejected':
      case 'ditolak': return '#ffe6e6';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'pending':
      case 'menunggu': return '#e67700';
      case 'approved':
      case 'disetujui': return '#2ec27e';
      case 'rejected':
      case 'ditolak': return '#c01c28';
      default: return 'var(--text-dark)';
    }
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-start;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }
`;

const DetailModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  z-index: 1000;
`;

const DetailCard = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border);
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const DetailHeaderContent = styled.div`
  flex: 1;
  margin-right: 1rem;
`;

const DetailStatus = styled(StatusBadge)`
  font-size: 0.875rem;
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DecisionMaker = styled.div`
  font-size: 0.75rem;
  color: var(--text-light);
  font-style: italic;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-light);
  padding: 0.25rem;
  line-height: 1;
  
  &:hover {
    color: var(--text-dark);
  }
`;

const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const DetailGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  grid-column: ${props => props.full ? '1 / -1' : 'auto'};
`;

const DetailLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-light);
`;

const ApproveButton = styled.button`
  flex: 1;
  background-color: var(--success, #2ec27e);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.938rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1.5rem;
  
  &:hover {
    background-color: #26a96b;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const RejectButton = styled(ApproveButton)`
  background-color: var(--danger, #c01c28);
  
  &:hover {
    background-color: #a01622;
  }
`;

const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1rem;
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  cursor: pointer;
  min-height: 120px;
  max-height: 120px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }
`;

const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  padding: 1rem;
  border-radius: 8px;
  background-color: ${props => props.type === 'success' ? 'var(--success, #2ec27e)' : 'var(--danger, #c01c28)'};
  color: white;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: slideIn 0.3s ease-out forwards;
  max-width: 350px;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const TeacherSuggestContainer = styled.div`
  position: relative;
`;

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: var(--background);
  border: 1px solid var(--border);
  border-top: none;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
`;

const SuggestionItem = styled.div`
  padding: 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid var(--border);
  transition: background-color 0.2s;
  background-color: ${props => props.selected ? 'var(--primary-light)' : 'transparent'};
  
  &:hover {
    background-color: var(--background-light);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TeacherName = styled.div`
  font-weight: 500;
  color: var(--text-dark);
`;

const BackButton = styled.button`
  background-color: var(--primary, #3b82f6);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;
  
  &:hover {
    background-color: var(--primary-dark, #2563eb);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

export default TeacherLaporanLemburPage;